import React, { useEffect, useMemo } from "react";

import { z } from "zod";

import { Icon } from "@braze/beacon-core";
import { useId } from "@braze/beacon-hooks";

import { assertElementIsActionable } from "@lib/agenticAi/assertElementIsActionable";

import useI18nNamespace from "@services/useI18nNamespace";
import { useRegisterChangeTool } from "@services/useRegisterChangeTool";

import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay/DiffDisplay";

import { useMergedRef } from "../useMergedRef";
import type { ClickableElement, UseClickToolParams, UseClickToolResult } from "./types";

/**
 * Register an Operator tool that clicks a rendered control.
 *
 * The tool dispatches a real click on the underlying DOM node, so Operator follows the
 * same path a person does: React's `onClick` runs, the event bubbles, a `type="submit"`
 * button runs its form's submission (constraint validation and submit handler included),
 * and an anchor navigates. Nothing here needs to know about forms or routing.
 *
 * @param params - Tool registration details and the consumer's forwarded ref.
 * @returns A ref callback to attach to the control, and the control's `operatorDescription`.
 */
export const useClickTool = <T extends ClickableElement>({
  forwardedRef,
  idPrefix,
  toolEntityId,
  name,
  description,
  subject,
  operatorLabel,
  operatorDisabledReason,
  operatorRequiresApproval = "yes",
  disabled = false,
  toolIconName = "hand-pointer",
}: UseClickToolParams<T>): UseClickToolResult<T> => {
  const { t } = useI18nNamespace("braze-button");
  const registerChangeTool = useRegisterChangeTool({ operatorDisabledReason });
  const generatedToolEntityId = useId(idPrefix);
  const resolvedToolEntityId = toolEntityId ?? generatedToolEntityId;

  const { elementRef, setRef } = useMergedRef<T>(forwardedRef);

  useEffect(() => {
    if (disabled) {
      return;
    }

    const cleanup = registerChangeTool({
      toolEntityId: resolvedToolEntityId,
      name,
      description,
      parameters: z.object({}),
      callback: () => {
        const element = elementRef.current;

        const isDisabled = element instanceof HTMLButtonElement && element.disabled;
        assertElementIsActionable(element, { disabled: isDisabled, readOnly: false }, subject, "clicked");

        element.click();

        return `${subject} was successfully clicked.`;
      },
      options: {
        toolGroup: "Agentic Creation & Editing",
        labels: {
          ready: t("click-button-ready", "Preparing to click"),
          running: t("click-button-running", "Clicking"),
          complete: t("click-button-complete", "Clicked"),
          failed: t("click-button-failed", "Failed to click"),
          stopped: t("click-button-stopped", "Stopped clicking"),
        },
        requiresApproval: operatorRequiresApproval,
        argumentsSummaryText: () => operatorLabel,
        renderApproval: () => {
          return (
            <DiffDisplay
              title={t("action-title", "Action")}
              modified={t("click-button-description", "Click button: {label}", { label: operatorLabel })}
            />
          );
        },
        toolIcon: <Icon name={toolIconName} />,
      },
    });

    return () => {
      cleanup();
    };
  }, [
    registerChangeTool,
    resolvedToolEntityId,
    name,
    description,
    subject,
    operatorLabel,
    operatorRequiresApproval,
    toolIconName,
    t,
    disabled,
    elementRef,
  ]);

  const operatorDescription = useMemo(() => {
    const button = document.createElement("button");
    button.id = resolvedToolEntityId;
    if (disabled) {
      button.disabled = true;
    }
    button.innerText = operatorLabel;
    return button.outerHTML;
  }, [resolvedToolEntityId, operatorLabel, disabled]);

  return { setRef, operatorDescription };
};
